import { motion, useReducedMotion } from "framer-motion";
import dgptLogo from "@/assets/dgptlogo.png";
import dgptMark from "@/assets/dgpt.png";

/**
 * Slow-drifting colour behind the conversation. Deliberately very low contrast
 * — it should register as depth, not decoration, and never compete with the
 * text it sits under.
 */
export function Backdrop() {
  const shouldReduce = useReducedMotion();

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32' width='32' height='32' fill='none' stroke='rgb(100 116 139 / 0.4)'%3e%3cpath d='M0 .5H31.5V32'/%3e%3c/svg%3e")`,
        }}
      />

      <motion.div
        className="absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-blue-500/8 blur-3xl"
        animate={shouldReduce ? {} : { x: [0, 40, 0], y: [0, 25, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -bottom-40 -right-32 h-[26rem] w-[26rem] rounded-full bg-indigo-500/8 blur-3xl"
        animate={shouldReduce ? {} : { x: [0, -35, 0], y: [0, -20, 0] }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut", delay: 3 }}
      />
    </div>
  );
}

/**
 * Full-screen state for the moment before the conversation can start —
 * either fetching state or provisioning the workspace.
 */
export function ProvisioningSplash({ label }: { label: string }) {
  const shouldReduce = useReducedMotion();

  return (
    <div className="relative flex h-full min-h-dvh items-center justify-center overflow-hidden bg-background lg:min-h-0">
      <Backdrop />

      <div className="relative z-10 flex flex-col items-center gap-6">
        <div className="relative flex h-20 w-20 items-center justify-center">
          {/* Two counter-rotating rings — reads as "working" without the
              literalism of a spinner. */}
          {!shouldReduce && (
            <>
              <motion.span
                className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary/70 border-r-primary/30"
                animate={{ rotate: 360 }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "linear" }}
              />
              <motion.span
                className="absolute inset-2 rounded-full border-2 border-transparent border-b-primary/40 border-l-primary/20"
                animate={{ rotate: -360 }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "linear" }}
              />
            </>
          )}
          {/* The square mark sits inside the rings; the wide wordmark can't. */}
          <motion.img
            src={dgptMark}
            alt=""
            className="h-11 w-11 rounded-full object-cover"
            animate={shouldReduce ? {} : { scale: [1, 1.07, 1], opacity: [0.85, 1, 0.85] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <img src={dgptLogo} alt="DiligenceGPT" className="h-6 w-auto" />

        <motion.p
          key={label}
          className="text-sm text-muted-foreground"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {label}
        </motion.p>
      </div>
    </div>
  );
}
